import React, { useEffect, useState, Suspense, lazy, createElement, Component } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { DataProvider } from './contexts/DataContext';
import { NotificationProvider, useNotifications } from './contexts/NotificationContext';
import { Layout } from './components/layout/Layout';
import { LoginPage } from './pages/LoginPage';
import { getToday } from './utils/helpers';
// Lazy-loaded modules to avoid compilation timeout
const Dashboard = lazy(() => import('./components/modules/Dashboard').then(m => ({
  default: m.Dashboard
})));
const Patients = lazy(() => import('./components/modules/Patients').then(m => ({
  default: m.Patients
})));
const Doctors = lazy(() => import('./components/modules/Doctors').then(m => ({
  default: m.Doctors
})));
const Appointments = lazy(() => import('./components/modules/Appointments').then(m => ({
  default: m.Appointments
})));
const Services = lazy(() => import('./components/modules/Services').then(m => ({
  default: m.Services
})));
const Insurance = lazy(() => import('./components/modules/Insurance').then(m => ({
  default: m.Insurance
})));
const Specialties = lazy(() => import('./components/modules/Specialties').then(m => ({
  default: m.Specialties
})));
const Contacts = lazy(() => import('./components/modules/Contacts').then(m => ({
  default: m.Contacts
})));
const Opportunities = lazy(() => import('./components/modules/Opportunities').then(m => ({
  default: m.Opportunities
})));
const Settings = lazy(() => import('./components/modules/Settings').then(m => ({
  default: m.Settings
})));
const Intelligence = lazy(() => import('./components/modules/Intelligence').then(m => ({
  default: m.Intelligence
})));
const Commissions = lazy(() => import('./components/modules/Commissions').then(m => ({
  default: m.Commissions
})));
const ClinicalHistory = lazy(() => import('./components/modules/ClinicalHistory').then(m => ({
  default: m.ClinicalHistory
})));
const AuditLog = lazy(() => import('./components/modules/AuditLog').then(m => ({
  default: m.AuditLog
})));
const FollowUpNotes = lazy(() => import('./components/modules/FollowUpNotes').then(m => ({
  default: m.FollowUpNotes
})));
// ==================== ERROR BOUNDARY ====================
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}
interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: 'full' | 'inline' | 'hidden';
  name?: string;
}
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const name = this.props.name || 'Unknown';
    console.error(`[ErrorBoundary:${name}] caught:`, error.message);
    if (errorInfo.componentStack) {
      console.error(`[ErrorBoundary:${name}] stack:`, errorInfo.componentStack);
    }
    // Auto-clear corrupted localStorage for #310 errors
    if (error.message && (error.message.includes('310') || error.message.includes('Objects are not valid'))) {
      console.warn(`[ErrorBoundary:${name}] Error #310 detected - clearing potentially corrupted data`);
      const keysToClean = ['savidna-notifications', 'savidna-audit-logs', 'savidna-follow-up-notes', 'savidna-audit-log-status', 'savidna-followup-notes-status', 'savidna-patients-status', 'savidna-doctors-status', 'savidna-services-status', 'savidna-specialties-status', 'savidna-insurance-status', 'savidna-contacts-status', 'savidna-companies-status', 'savidna-opportunities-status', 'savidna-clinical-history-status'];
      keysToClean.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch {}
      });
    }
  }
  render() {
    if (this.state.hasError) {
      const fallback = this.props.fallback || 'full';
      const name = this.props.name || '';
      // Hidden: silently swallow the error (for non-critical sections)
      if (fallback === 'hidden') return null;
      // Inline: show a small error message within the page
      if (fallback === 'inline') {
        return createElement('div', {
          style: {
            padding: '24px',
            margin: '16px 0',
            background: '#fef2f2',
            borderRadius: '12px',
            border: '1px solid #fecaca',
            textAlign: 'center' as const,
            fontFamily: 'system-ui, sans-serif'
          }
        }, createElement('p', {
          style: {
            color: '#991b1b',
            fontWeight: '600',
            fontSize: '14px',
            marginBottom: '8px'
          }
        }, `Error al cargar ${name || 'esta sección'}`), createElement('p', {
          style: {
            color: '#b91c1c',
            fontSize: '12px',
            marginBottom: '12px',
            fontFamily: 'monospace',
            background: '#fee2e2',
            padding: '8px 12px',
            borderRadius: '6px',
            textAlign: 'left' as const,
            wordBreak: 'break-all' as const,
            maxHeight: '120px',
            overflow: 'auto'
          }
        }, String(this.state.error?.message || 'Error desconocido')), createElement('button', {
          onClick: () => this.setState({
            hasError: false,
            error: null
          }),
          style: {
            padding: '6px 16px',
            borderRadius: '8px',
            border: '1px solid #fca5a5',
            background: 'white',
            color: '#dc2626',
            fontWeight: '500',
            cursor: 'pointer',
            fontSize: '13px'
          }
        }, 'Reintentar'));
      }
      // Full: show the full error page (for the outermost boundary)
      return createElement('div', {
        style: {
          padding: '40px',
          fontFamily: 'system-ui, sans-serif',
          maxWidth: '600px',
          margin: '80px auto',
          textAlign: 'center' as const
        }
      }, createElement('div', {
        style: {
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #14b8a6, #06b6d4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px'
        }
      }, createElement('svg', {
        width: '32',
        height: '32',
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'white',
        strokeWidth: '2',
        strokeLinecap: 'round',
        strokeLinejoin: 'round'
      }, createElement('path', {
        d: 'M12 21c-4-4-8-6.5-8-10.5a4.5 4.5 0 0 1 9 0 4.5 4.5 0 0 1 9 0c0 4-4 6.5-8 10.5'
      }))), createElement('h1', {
        style: {
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#111827',
          marginBottom: '8px'
        }
      }, 'Savidna - Error Temporal'), createElement('p', {
        style: {
          color: '#6b7280',
          marginBottom: '24px',
          lineHeight: '1.6'
        }
      }, 'Ocurrió un error al cargar la aplicación. Esto puede deberse a datos en caché.'), createElement('div', {
        style: {
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap' as const
        }
      }, createElement('button', {
        onClick: () => this.setState({
          hasError: false,
          error: null
        }),
        style: {
          padding: '10px 24px',
          borderRadius: '10px',
          border: 'none',
          background: 'linear-gradient(135deg, #14b8a6, #06b6d4)',
          color: 'white',
          fontWeight: '600',
          cursor: 'pointer',
          fontSize: '14px'
        }
      }, 'Reintentar'), createElement('button', {
        onClick: () => {
          // Clear ALL savidna localStorage except theme
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('savidna-') && key !== 'savidna-theme') {
              localStorage.removeItem(key);
            }
          });
          window.location.reload();
        },
        style: {
          padding: '10px 24px',
          borderRadius: '10px',
          border: '1px solid #d1d5db',
          background: 'white',
          color: '#374151',
          fontWeight: '600',
          cursor: 'pointer',
          fontSize: '14px'
        }
      }, 'Limpiar Caché y Recargar')), createElement('details', {
        style: {
          marginTop: '24px',
          textAlign: 'left' as const,
          background: '#f9fafb',
          borderRadius: '8px',
          padding: '16px'
        }
      }, createElement('summary', {
        style: {
          cursor: 'pointer',
          color: '#6b7280',
          fontSize: '13px',
          fontWeight: '500'
        }
      }, 'Detalles técnicos'), createElement('pre', {
        style: {
          marginTop: '8px',
          fontSize: '12px',
          color: '#ef4444',
          whiteSpace: 'pre-wrap' as const,
          wordBreak: 'break-all' as const
        }
      }, String(this.state.error?.message || '') + '\n\n' + String(this.state.error?.stack || ''))));
    }
    return this.props.children;
  }
}
// ==================== SAFE MODULE WRAPPER ====================
// Wraps each module in its own error boundary so one broken module
// can never crash the entire application
function SafeModule({
  name,
  children
}: {
  name: string;
  children: React.ReactNode;
}) {
  return <ErrorBoundary fallback="inline" name={name} data-id="element-0">
      {children}
    </ErrorBoundary>;
}
// ==================== SESSION EXPIRED OVERLAY ====================
function SessionExpiredOverlay() {
  const {
    sessionExpired,
    sessionWarning,
    sessionSecondsLeft,
    logout,
    extendCurrentSession
  } = useAuth();
  // Format seconds as mm:ss
  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  // Warning overlay - session about to expire
  if (sessionWarning && !sessionExpired) {
    return <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-1">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" data-id="element-2">
          <div className="h-1.5 bg-gradient-to-r from-orange-400 to-amber-500" data-id="element-3" />
          <div className="p-8 text-center" data-id="element-4">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center" data-id="element-5">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-500 dark:text-orange-400" data-id="element-6">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" data-id="element-7" />
                <line x1="12" y1="9" x2="12" y2="13" data-id="element-8" />
                <line x1="12" y1="17" x2="12.01" y2="17" data-id="element-9" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-10">
              ¿Sigues ahí?
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-3 leading-relaxed" data-id="element-11">
              Tu sesión está a punto de expirar por inactividad.
            </p>
            <div className="text-4xl font-bold text-orange-500 dark:text-orange-400 mb-6 tabular-nums" data-id="element-12">
              {formatTime(sessionSecondsLeft)}
            </div>
            <div className="flex gap-3" data-id="element-13">
              <button onClick={logout} className="flex-1 px-4 py-3 rounded-xl font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200" data-id="element-14">
                Cerrar Sesión
              </button>
              <button onClick={extendCurrentSession} className="flex-1 px-4 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 shadow-lg shadow-teal-500/20 transition-all duration-200" data-id="element-15">
                Continuar
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  // Expired overlay
  if (!sessionExpired) return null;
  return <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-id="element-16">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" data-id="element-17">
        <div className="h-1.5 bg-gradient-to-r from-amber-500 to-orange-500" data-id="element-18" />
        <div className="p-8 text-center" data-id="element-19">
          <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center" data-id="element-20">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600 dark:text-amber-400" data-id="element-21">
              <circle cx="12" cy="12" r="10" data-id="element-22" />
              <polyline points="12 6 12 12 16 14" data-id="element-23" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-24">
            Sesión Expirada
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6 leading-relaxed" data-id="element-25">
            Tu sesión expiró por inactividad. Por favor, inicia sesión
            nuevamente para continuar.
          </p>
          <button onClick={logout} className="w-full px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 shadow-lg shadow-teal-500/20 transition-all duration-200" data-id="element-26">
            Iniciar Sesión de Nuevo
          </button>
        </div>
      </div>
    </div>;
}
// ==================== APP CONTENT ====================
function AppContent() {
  const {
    isAuthenticated,
    isLoading
  } = useAuth();
  const {
    addNotification
  } = useNotifications();
  const [currentModule, setCurrentModule] = useState('dashboard');
  const [moduleParams, setModuleParams] = useState<Record<string, string>>({});
  // Wrapper to handle module navigation with optional params (e.g., 'appointments:today')
  const handleNavigate = (moduleStr: string) => {
    if (moduleStr.includes(':')) {
      const [mod, param] = moduleStr.split(':');
      setCurrentModule(mod);
      setModuleParams({
        filter: param
      });
    } else {
      setCurrentModule(moduleStr);
      setModuleParams({});
    }
  };
  // Dynamic page title
  useEffect(() => {
    const moduleNames: Record<string, string> = {
      dashboard: 'Panel de Control',
      patients: 'Pacientes',
      doctors: 'Doctores',
      appointments: 'Citas',
      'clinical-history': 'Historial Clínico',
      services: 'Servicios',
      insurance: 'Seguros',
      specialties: 'Especialidades',
      contacts: 'Contactos',
      opportunities: 'Oportunidades',
      'follow-up-notes': 'Notas de Seguimiento',
      'audit-log': 'Bitácora',
      intelligence: 'Inteligencia',
      commissions: 'Comisiones',
      settings: 'Configuración'
    };
    const moduleName = moduleNames[currentModule] || 'Panel de Control';
    document.title = `Savidna CRM — ${moduleName}`;
  }, [currentModule]);
  useEffect(() => {
    if (isAuthenticated) {
      setCurrentModule('dashboard');
      setModuleParams({});
    }
  }, [isAuthenticated]);
  useEffect(() => {
    ;
    (window as any).addNotification = addNotification;
    return () => {
      delete (window as any).addNotification;
    };
  }, [addNotification]);
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900" data-id="element-27">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500" data-id="element-28"></div>
      </div>;
  }
  if (!isAuthenticated) {
    return <>
        <SessionExpiredOverlay data-id="element-29" />
        <LoginPage data-id="element-30" />
      </>;
  }
  const renderModule = () => {
    switch (currentModule) {
      case 'dashboard':
        return <SafeModule name="Dashboard" data-id="element-31">
            <Suspense fallback={<ModuleLoader data-id="element-33" />} data-id="element-32">
              <Dashboard onNavigate={handleNavigate} data-id="element-34" />
            </Suspense>
          </SafeModule>;
      case 'patients':
        return <SafeModule name="Pacientes" data-id="element-35">
            <Suspense fallback={<ModuleLoader data-id="element-37" />} data-id="element-36">
              <Patients data-id="element-38" />
            </Suspense>
          </SafeModule>;
      case 'doctors':
        return <SafeModule name="Doctores" data-id="element-39">
            <Suspense fallback={<ModuleLoader data-id="element-41" />} data-id="element-40">
              <Doctors data-id="element-42" />
            </Suspense>
          </SafeModule>;
      case 'appointments':
        return <SafeModule name="Citas" data-id="element-43">
            <Suspense fallback={<ModuleLoader data-id="element-45" />} data-id="element-44">
              <Appointments initialDateFilter={moduleParams.filter === 'today' ? getToday() : undefined} data-id="element-46" />
            </Suspense>
          </SafeModule>;
      case 'clinical-history':
        return <SafeModule name="Historial Clínico" data-id="element-47">
            <Suspense fallback={<ModuleLoader data-id="element-49" />} data-id="element-48">
              <ClinicalHistory data-id="element-50" />
            </Suspense>
          </SafeModule>;
      case 'services':
        return <SafeModule name="Servicios" data-id="element-51">
            <Suspense fallback={<ModuleLoader data-id="element-53" />} data-id="element-52">
              <Services data-id="element-54" />
            </Suspense>
          </SafeModule>;
      case 'insurance':
        return <SafeModule name="Seguros" data-id="element-55">
            <Suspense fallback={<ModuleLoader data-id="element-57" />} data-id="element-56">
              <Insurance data-id="element-58" />
            </Suspense>
          </SafeModule>;
      case 'specialties':
        return <SafeModule name="Especialidades" data-id="element-59">
            <Suspense fallback={<ModuleLoader data-id="element-61" />} data-id="element-60">
              <Specialties data-id="element-62" />
            </Suspense>
          </SafeModule>;
      case 'contacts':
        return <SafeModule name="Contactos" data-id="element-63">
            <Suspense fallback={<ModuleLoader data-id="element-65" />} data-id="element-64">
              <Contacts data-id="element-66" />
            </Suspense>
          </SafeModule>;
      case 'opportunities':
        return <SafeModule name="Oportunidades" data-id="element-67">
            <Suspense fallback={<ModuleLoader data-id="element-69" />} data-id="element-68">
              <Opportunities data-id="element-70" />
            </Suspense>
          </SafeModule>;
      case 'follow-up-notes':
        return <SafeModule name="Notas de Seguimiento" data-id="element-71">
            <Suspense fallback={<ModuleLoader data-id="element-73" />} data-id="element-72">
              <FollowUpNotes data-id="element-74" />
            </Suspense>
          </SafeModule>;
      case 'audit-log':
        return <SafeModule name="Bitácora" data-id="element-75">
            <Suspense fallback={<ModuleLoader data-id="element-77" />} data-id="element-76">
              <AuditLog data-id="element-78" />
            </Suspense>
          </SafeModule>;
      case 'intelligence':
        return <SafeModule name="Inteligencia" data-id="element-79">
            <Suspense fallback={<ModuleLoader data-id="element-81" />} data-id="element-80">
              <Intelligence data-id="element-82" />
            </Suspense>
          </SafeModule>;
      case 'commissions':
        return <SafeModule name="Comisiones" data-id="element-83">
            <Suspense fallback={<ModuleLoader data-id="element-85" />} data-id="element-84">
              <Commissions data-id="element-86" />
            </Suspense>
          </SafeModule>;
      case 'settings':
        return <SafeModule name="Configuración" data-id="element-87">
            <Suspense fallback={<ModuleLoader data-id="element-89" />} data-id="element-88">
              <Settings data-id="element-90" />
            </Suspense>
          </SafeModule>;
      default:
        return <SafeModule name="Dashboard" data-id="element-91">
            <Suspense fallback={<ModuleLoader data-id="element-93" />} data-id="element-92">
              <Dashboard onNavigate={handleNavigate} data-id="element-94" />
            </Suspense>
          </SafeModule>;
    }
  };
  return <ErrorBoundary fallback="inline" name="Layout" data-id="element-95">
      <SessionExpiredOverlay data-id="element-96" />
      <Layout currentModule={currentModule} onModuleChange={handleNavigate} data-id="element-97">
        {renderModule()}
      </Layout>
    </ErrorBoundary>;
}
// ==================== PROVIDERS WRAPPER ====================
// Each provider is wrapped in its own error boundary so a failure
// in one provider doesn't take down the entire app
function SafeProviders({
  children
}: {
  children: React.ReactNode;
}) {
  return <ErrorBoundary fallback="full" name="ThemeProvider" data-id="element-98">
      <ThemeProvider data-id="element-99">
        <ErrorBoundary fallback="full" name="AuthProvider" data-id="element-100">
          <AuthProvider data-id="element-101">
            <ErrorBoundary fallback="full" name="NotificationProvider" data-id="element-102">
              <NotificationProvider data-id="element-103">
                <ErrorBoundary fallback="full" name="DataProvider" data-id="element-104">
                  <DataProvider data-id="element-105">{children}</DataProvider>
                </ErrorBoundary>
              </NotificationProvider>
            </ErrorBoundary>
          </AuthProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </ErrorBoundary>;
}
// ==================== APP ROOT ====================
export function App() {
  return <ErrorBoundary fallback="full" name="App" data-id="element-106">
      <SafeProviders data-id="element-107">
        <AppContent data-id="element-108" />
      </SafeProviders>
    </ErrorBoundary>;
}
// Lazy loading fallback spinner
function ModuleLoader() {
  return <div className="flex items-center justify-center py-20" data-id="element-109">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-500" data-id="element-110"></div>
    </div>;
}